// ===================================
// TRENCH QUEST - Game Session Firebase Service
// Persistence for game state
// ===================================

import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  Timestamp,
  serverTimestamp,
} from 'firebase/firestore'
import { db } from './config'
import type {
  GameSession,
  Trade,
  GameEvent,
  Portfolio,
  MarketState,
  GameTime,
} from '@/types'

const SESSIONS_COLLECTION = 'games'

// Convert Firestore data to GameSession
const toGameSession = (id: string, data: any): GameSession => {
  return {
    id,
    owner: data.owner,
    status: data.status,
    difficulty: data.difficulty,
    startedAt: data.startedAt?.toDate() || new Date(),
    lastActiveAt: data.lastActiveAt?.toDate() || new Date(),
    currentDay: data.currentDay,
    startingToken: data.startingToken || 'USDC',
    startingAmount: data.startingAmount,
    goal: {
      ...data.goal,
      deadline: data.goal?.deadline?.toDate() || new Date(),
    },
    world: data.world,
    player: {
      ...data.player,
      createdAt: data.player?.createdAt?.toDate() || new Date(),
      lastActiveAt: data.player?.lastActiveAt?.toDate() || new Date(),
    },
    gameTime: data.gameTime,
    market: data.market,
    tradeHistory: (data.tradeHistory || []).map((t: any) => ({
      ...t,
      timestamp: t.timestamp?.toDate() || new Date(),
    })),
    decisionCount: data.decisionCount || 0,
  }
}

// Convert GameSession to Firestore data
const toFirestoreData = (session: GameSession): any => {
  return {
    owner: session.owner,
    status: session.status,
    difficulty: session.difficulty,
    startedAt: Timestamp.fromDate(session.startedAt),
    lastActiveAt: serverTimestamp(),
    currentDay: session.currentDay,
    startingToken: session.startingToken,
    startingAmount: session.startingAmount,
    goal: {
      ...session.goal,
      deadline: Timestamp.fromDate(session.goal.deadline),
    },
    world: session.world,
    player: {
      ...session.player,
      createdAt: Timestamp.fromDate(session.player.createdAt),
      lastActiveAt: serverTimestamp(),
    },
    gameTime: session.gameTime,
    market: session.market,
    tradeHistory: session.tradeHistory.map(t => ({
      ...t,
      timestamp: Timestamp.fromDate(t.timestamp),
    })),
    decisionCount: session.decisionCount || 0,
  }
}

export class GameSessionService {
  // Create a new game session
  async createSession(session: GameSession): Promise<GameSession> {
    const docRef = doc(collection(db, SESSIONS_COLLECTION))
    const sessionWithId = { ...session, id: docRef.id }
    await setDoc(docRef, toFirestoreData(sessionWithId))
    return sessionWithId
  }

  // Get a session by ID
  async getSession(sessionId: string): Promise<GameSession | null> {
    const docRef = doc(db, SESSIONS_COLLECTION, sessionId)
    const snapshot = await getDoc(docRef)

    if (!snapshot.exists()) {
      return null
    }

    return toGameSession(snapshot.id, snapshot.data())
  }

  // Get active session for a wallet
  async getActiveSession(walletAddress: string): Promise<GameSession | null> {
    const q = query(
      collection(db, SESSIONS_COLLECTION),
      where('owner', '==', walletAddress),
      where('status', '==', 'active'),
      orderBy('lastActiveAt', 'desc'),
      limit(1)
    )

    const snapshot = await getDocs(q)

    if (snapshot.empty) {
      return null
    }

    const firstDoc = snapshot.docs[0]
    if (!firstDoc) return null
    return toGameSession(firstDoc.id, firstDoc.data())
  }

  // Get all sessions for a wallet
  async getSessionHistory(
    walletAddress: string,
    limitCount: number = 10
  ): Promise<GameSession[]> {
    const q = query(
      collection(db, SESSIONS_COLLECTION),
      where('owner', '==', walletAddress),
      orderBy('startedAt', 'desc'),
      limit(limitCount)
    )

    const snapshot = await getDocs(q)
    return snapshot.docs.map(doc => toGameSession(doc.id, doc.data()))
  }

  // Update session state
  async updateSession(
    sessionId: string,
    updates: Partial<GameSession>
  ): Promise<void> {
    const docRef = doc(db, SESSIONS_COLLECTION, sessionId)

    // Convert dates and timestamps
    const firestoreUpdates: any = { ...updates, lastActiveAt: serverTimestamp() }

    if (updates.goal?.deadline) {
      firestoreUpdates.goal = {
        ...updates.goal,
        deadline: Timestamp.fromDate(updates.goal.deadline),
      }
    }

    if (updates.player?.lastActiveAt) {
      firestoreUpdates.player = {
        ...updates.player,
        lastActiveAt: serverTimestamp(),
      }
    }

    await updateDoc(docRef, firestoreUpdates)
  }

  // Update portfolio
  async updatePortfolio(sessionId: string, portfolio: Portfolio): Promise<void> {
    const docRef = doc(db, SESSIONS_COLLECTION, sessionId)
    await updateDoc(docRef, {
      'player.portfolio': portfolio,
      lastActiveAt: serverTimestamp(),
    })
  }

  // Update market state
  async updateMarket(sessionId: string, market: MarketState): Promise<void> {
    const docRef = doc(db, SESSIONS_COLLECTION, sessionId)
    await updateDoc(docRef, {
      market,
      lastActiveAt: serverTimestamp(),
    })
  }

  // Update game time
  async updateGameTime(sessionId: string, gameTime: GameTime): Promise<void> {
    const docRef = doc(db, SESSIONS_COLLECTION, sessionId)
    await updateDoc(docRef, {
      gameTime,
      currentDay: gameTime.gameDay,
      lastActiveAt: serverTimestamp(),
    })
  }

  // Add trade to history
  async addTrade(sessionId: string, trade: Trade): Promise<void> {
    const session = await this.getSession(sessionId)
    if (!session) throw new Error('Session not found')

    const updatedHistory = [...session.tradeHistory, trade]
    const docRef = doc(db, SESSIONS_COLLECTION, sessionId)
    await updateDoc(docRef, {
      tradeHistory: updatedHistory.map(t => ({
        ...t,
        timestamp: Timestamp.fromDate(t.timestamp),
      })),
      lastActiveAt: serverTimestamp(),
    })
  }

  // End game (win/lose)
  async endGame(
    sessionId: string,
    status: 'won' | 'lost' | 'abandoned',
    finalPortfolioValue: number
  ): Promise<void> {
    const docRef = doc(db, SESSIONS_COLLECTION, sessionId)
    await updateDoc(docRef, {
      status,
      endedAt: serverTimestamp(),
      finalPortfolioValue,
      lastActiveAt: serverTimestamp(),
    })
  }

  // Save event to session
  async saveEvent(sessionId: string, event: GameEvent): Promise<void> {
    const session = await this.getSession(sessionId)
    if (!session) throw new Error('Session not found')

    const docRef = doc(db, SESSIONS_COLLECTION, sessionId)
    await updateDoc(docRef, {
      currentEvent: {
        ...event,
        timestamp: Timestamp.fromDate(event.timestamp),
      },
      lastActiveAt: serverTimestamp(),
    })
  }
}

export const gameSessionService = new GameSessionService()
